import { createClient, type SupabaseClient } from '@supabase/supabase-js'
import type { Database } from './database.types'

// ── Multi-tenancy ────────────────────────────────────────────
// Tenant = district. Today the deployment is shared-database /
// shared-schema: a single Supabase project holds every district and
// Row Level Security (district_id + public.my_district_id()) isolates
// them. `clientForTenant()` is the single seam through which all data
// access flows, so moving to database-per-tenant later is a localized
// change: resolve the tenant's project URL/key here and return its
// client. Callers never assume a single global database.

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY

if (!supabaseUrl || !supabaseAnonKey) {
  console.error(
    'Missing Supabase environment variables. Copy .env.example to .env and fill in your project credentials.'
  )
}

function makeClient(url: string, key: string): SupabaseClient<Database> {
  return createClient<Database>(url ?? '', key ?? '', {
    auth: { persistSession: true, autoRefreshToken: true },
  })
}

// The shared-tenant client (current architecture).
export const supabase = makeClient(supabaseUrl ?? '', supabaseAnonKey ?? '')

/**
 * Returns the Supabase client for a tenant (district).
 *
 * Shared-database today: always the shared client. To split tenants into
 * separate databases in the future, map `districtId`/host → that tenant's
 * project URL + anon key here and return a cached per-tenant client. This
 * is the only place that needs to change.
 */
export function clientForTenant(_districtId?: string): SupabaseClient<Database> {
  return supabase
}

// ── Storage helpers ──────────────────────────────────────────

export const EVIDENCE_BUCKET = 'evidence'

export function evidenceStoragePath(
  districtId: string,
  iplpId: string,
  componentCode: string,
  filename: string
): string {
  return `${districtId}/${iplpId}/${componentCode}/${filename}`
}

export async function uploadEvidence(
  districtId: string,
  iplpId: string,
  componentCode: string,
  file: File
): Promise<{ path: string; publicUrl: string } | null> {
  const ext = file.name.split('.').pop()
  const filename = `${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`
  const path = evidenceStoragePath(districtId, iplpId, componentCode, filename)

  const { error } = await supabase.storage
    .from(EVIDENCE_BUCKET)
    .upload(path, file, { contentType: file.type, upsert: false })

  if (error) {
    console.error('Upload error:', error)
    return null
  }

  const { data } = supabase.storage.from(EVIDENCE_BUCKET).getPublicUrl(path)
  return { path, publicUrl: data.publicUrl }
}

export async function deleteEvidence(storagePath: string): Promise<boolean> {
  const { error } = await supabase.storage.from(EVIDENCE_BUCKET).remove([storagePath])
  return !error
}

export function getEvidenceUrl(storagePath: string): string {
  const { data } = supabase.storage.from(EVIDENCE_BUCKET).getPublicUrl(storagePath)
  return data.publicUrl
}

// ── Edge function caller ─────────────────────────────────────

export interface InviteStaffPayload {
  email: string
  name: string
  role: 'building_admin' | 'teacher' | 'support' | 'classified'
  school_id: string | null
  subject: string | null
  years_certified: number
  evaluator_id: string | null
  form_template_id: string | null
  school_year: string
}

export async function callInviteStaff(
  payload: InviteStaffPayload
): Promise<{ success: boolean; user_id?: string; error?: string }> {
  const { data, error } = await supabase.functions.invoke('invite-staff', {
    body: payload,
  })
  if (error) {
    // Edge functions surface non-2xx bodies through the error context.
    const ctx = (error as { context?: Response }).context
    if (ctx) {
      try {
        const body = await ctx.json()
        return { success: false, error: body.error ?? error.message }
      } catch { /* fall through */ }
    }
    return { success: false, error: error.message }
  }
  return data as { success: boolean; user_id?: string; error?: string }
}

export async function callSendReminder(payload: {
  recipient_id: string
  iplp_id: string
  channel: 'email' | 'in_app'
  message: string
}): Promise<{ success: boolean; error?: string }> {
  const { data, error } = await supabase.functions.invoke('send-reminder', {
    body: payload,
  })
  if (error) return { success: false, error: error.message }
  return data as { success: boolean }
}
